<template>
  <div class="compare-table compare-table--collaborations">
    <div
      id="compareElement"
      ref="compareElement"
      class="compare-table-element--collaborations white box-shadow-soft"
    >
      <template v-if="isEverythingReady">
        <!-- Basic profile stats -->
        <div
          v-for="(overviewSection, sectionIndex) in overviewSections[0]"
          :key="'overview-section-' + sectionIndex"
          class="box"
        >
          <!-- 0. Profile pictures and names -->
          <v-row class="px-3" v-if="sectionIndex === 0">
            <v-col cols="3" lg="2" class="px-0 bluegray d-flex align-end">
              <div class="text-h6 pl-4 primary--text">
                Overview
              </div>
            </v-col>
            <v-col cols="9" lg="10" class="px-0 py-8 d-flex align-end"></v-col>
          </v-row>

          <!-- 1: Basic Profile Stats -->
          <!-- a 2x10 grid -->
          <v-row class="px-3">
            <v-col cols="3" lg="2" class="px-0 py-0 bluegray">
              <v-list dense color="bluegray">
                <!-- go through the object and get the text labels -->
                <template
                  v-for="(item, index) in overviewSection"
                >
                  <v-list-item
                    :key="'overview-text-' + index"
                    class="text-body-2 white--text"
                  >
                    <v-list-item-title>
                      {{ item.text }}
                    </v-list-item-title>
                    <v-list-item-action v-if="item.tooltip">
                      <v-tooltip right>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            color="white"
                            v-bind="attrs"
                            v-on="on"
                            small
                          >
                            help
                          </v-icon>
                        </template>
                        <span>
                          {{ item.tooltip }}
                        </span>
                      </v-tooltip>
                    </v-list-item-action>
                  </v-list-item>

                  <v-divider
                    v-if="(index + 1) < overviewSection.length"
                    :key="'overview-text-divider-' + index"
                  ></v-divider>
                </template>
              </v-list>
            </v-col>

            <v-col cols="9" lg="10" class="px-0">
              <!-- show equal width 2 or 3 items -->
              <v-row class="px-3">
                <v-col
                  :cols="colSize"
                  v-for="(overviewSection, index) in overviewSections"
                  :key="'stats-' + index"
                  class="px-0"
                >
                  <v-list
                    dense
                    class="text-body-2 font-weight-medium"
                  >
                    <!-- go through the object and get the text labels -->
                    <template
                      v-for="(item, _index) in overviewSection[sectionIndex]"
                    >
                      <v-list-item
                        :key="'overview-value-' + index + '-' + _index"
                        class="px-3 d-flex justify-center bluegray--text"
                      >
                        <!-- show the flag for country filter -->
                        <span
                          v-if="item.countryCode"
                          class="mr-1"
                        >
                          <v-img
                            height="13"
                            width="20"
                            :src="proxyUrl(`https://flagcdn.com/w20/${item.countryCode.toLowerCase()}.png`, true)"
                          />
                        </span>

                        <!-- show the text value -->
                        <span>
                          {{ item.value || '-' }}
                        </span>
                      </v-list-item>

                      <v-divider
                        v-if="(_index + 1) < overviewSection[sectionIndex].length"
                        :key="'overview-value-divider-' + index + '-' + _index"
                      ></v-divider>
                    </template>
                  </v-list>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </div>

        <!-- Posts -->
        <v-row class="px-3">
          <v-col cols="3" lg="2" class="px-0 bluegray pt-12">
            <div class="text-h6 pl-4 primary--text">
              Posts
            </div>
          </v-col>

          <v-col cols="9" lg="10" class="pt-4">
            <template>
              <div
                v-for="(posts, itemIndex) in postValues"
                :key="'item-index-' + itemIndex"
                :class="{ 'mt-8': itemIndex !== 0  }"
              >
                <template v-if="posts.length">
                  <div
                    class="box"
                    v-for="(boxItem, boxIndex) in Math.ceil(posts.length / 3)"
                    :key="'post-account-' + itemIndex + '-box-' + boxItem"
                  >
                    <div
                      v-if="boxIndex === 0"
                      class="text-subtitle-1 font-weight-bold bluegray--text mb-4 pt-8"
                    >
                      Posts from search: {{ postTitles[itemIndex] || '#' + itemIndex + 1}}
                    </div>

                    <v-row class="post-row">
                      <v-col
                        v-for="(item, postIndex) in posts.slice((boxItem - 1) * 3, boxItem * 3)"
                        :key="'post-account-' + itemIndex + '-post-item-' + postIndex"
                        cols="4"
                      >
                        <v-card class="box-shadow-sm">
                          <div class="d-flex flex-column post-card">
                            <!-- show the image attached with the post -->
                            <img
                              class="post-card__thumbnail"
                              :style="{ 'background-image': `url(${item.thumbnail})` }"
                            />

                            <!-- present the user's and post details -->
                            <v-card-text class="flex-grow-1">
                              <!-- the post user -->
                              <div class="mt-3">
                                <b>@{{ item.user.username }}</b>&nbsp;
                                ({{ nFormatter(item.user.followers) }} followers)
                              </div>
                              <!-- the post date -->
                              <div class="caption mt-2">
                                {{ dayjs(item.created).format("LLL") }}
                              </div>

                              <div class="mt-3 black--text">
                                {{ (item.text || '').slice(0, 88) + '...' }}
                              </div>
                            </v-card-text>

                            <v-card-actions>
                              <!-- show likes and comments stats -->
                              <v-row class="px-3 text-subtitle-2">
                                <!-- likes -->
                                <v-col>
                                  <div>
                                    Likes
                                  </div>
                                  <div class="black--text font-weight-bold">
                                    {{ nFormatter(item.stat.likes) }}
                                  </div>
                                </v-col>
                                <!-- Views -->
                                <v-col v-if="item.type === 'video'">
                                  <div>
                                    Views
                                  </div>
                                  <div class="black--text font-weight-bold">
                                    {{ nFormatter(item.stat.views) }}
                                  </div>
                                </v-col>
                                <!-- comments -->
                                <v-col>
                                  <div>
                                    Comments
                                  </div>
                                  <div class="black--text font-weight-bold">
                                    {{ nFormatter(item.stat.comments) }}
                                  </div>
                                </v-col>
                              </v-row>
                            </v-card-actions>
                          </div>
                        </v-card>
                      </v-col>
                    </v-row>
                  </div>
                </template>
              </div>
            </template>
          </v-col>
        </v-row>
      </template>
    </div>

    <!-- the download button -->
    <!-- <v-tooltip left>
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          v-if="isEverythingReady"
          :loading="isDownloading"
          :disabled="isDownloading"
          @click="handleDownloadStart"
          color="primary"
          v-bind="attrs"
          v-on="on"
          bottom
          fixed
          right
          fab
        >
          <v-icon>
            download
          </v-icon>
        </v-btn>
      </template>

      <span>
        Download report as PDF
      </span>
    </v-tooltip> -->
  </div>
</template>

<script>
// Import node packages
import download from "downloadjs"
import uniqBy from "lodash/uniqBy"

// Import select options generator
import filterOptions from "@/helpers/paid-collaborations/filterOptions.js"

// Export the SFC
export default {
  // Name of the component
  name: "CompareTable",

  // Accept incoming data from parent
  props: {
    items: {
      type: Array,
      required: true
    }
  },

  // Define local data variables
  data: () => ({
    isEverythingReady: false,
    isDownloading: false,
    downloadProgress: 0,

    filterOptions: filterOptions(),

    overviewSections: [],
    postValues: [],
    postTitles: [],
  }),

  // Define readonly computed variables
  computed: {
    shouldUseSlider() {
      return this.isLargeDevice
    },

    colSize() {
      return this.items.length === 2 ? 6 : 4
    },
  },

  // Define method functions
  methods: {
    handleDownloadStart() {
      // @TBD
    }
  },

  mounted() {
    // compute everything that's to be shown in the table
    for (const id of this.items) {
      // get the vuex values for this particular search ID
      // filter out any possible undefined or null values, only keep objects
      const posts = (this.$store.getters["paidCollaborations/posts"](id)?.data || []).filter((postItem) => typeof postItem === "object")

      // to be used to show the first 9 posts for each account
      this.postValues.push(posts.slice(0, 9))

      // compute the filter values which need to be shown
      const appliedFilters = {
        location: this.$store.getters["paidCollaborations/findFilterByType"](id, "location"),
        created: this.$store.getters["paidCollaborations/findFilterByType"](id, "created"),
        sponsor: this.$store.getters["paidCollaborations/findFilterByType"](id, "sponsor"),
        mentions: this.$store.getters["paidCollaborations/findFiltersByType"](id, "mention"),
        hashtags: this.$store.getters["paidCollaborations/findFiltersByType"](id, "hashtag")
      }

      // put the overview values
      const overviewSection = []

      // it will have about 3 groups of overview list items (3 pages)
      overviewSection.push([
        // Show the location's country name if the filter was set
        {
          text: "Location",
          value: !appliedFilters.location ? null : appliedFilters.location.data.inputs.name,
          countryCode: !appliedFilters.location ? null : appliedFilters.location.data.inputs.country_code
        },
        // Show the date range that was selected for these
        {
          text: "Date Range",
          // since this value is always set
          value: `${dayjs(appliedFilters.created.data.inputs.from).format('ll')} - ${dayjs(appliedFilters.created.data.inputs.to).format('ll')}`
        },
        // Show sponsored username if selected
        {
          text: "Sponsor",
          // since this value is always set
          value: !appliedFilters.sponsor ? null : (
            // If the "Any" checkbox is selected, don't show the username mentioned
            appliedFilters.sponsor.data.inputs.any ? "Any" : appliedFilters.sponsor.data.inputs.username
          )
        },
        // Show the mentioned usernames one by one
        {
          text: "Mentions",
          // since this value is always set
          value: !appliedFilters.mentions.length ? null : '@' + appliedFilters.mentions.map((item) => item.data.inputs.username).join(" @")
        },
        // Show the mentioned hashtags one by one
        {
          text: "Hashtags",
          // since this value is always set
          value: !appliedFilters.hashtags.length ? null : '#' + appliedFilters.hashtags.map((item) => item.data.inputs.hashtag).join(" #")
        }
      ])

      // for each filter group
      let index = 0
      // start at one because there's already some data in there
      let iterator = 1

      for (const selectedFilter of this.filterOptions) {
        const selectedItems = posts.filter((post) => {
          return post.user.followers >= selectedFilter.min && post.user.followers < selectedFilter.max
        })

        const nonDuplicateAccounts = uniqBy(selectedItems, "user.user_id")

        const followers = nonDuplicateAccounts.reduce((a, b) => a + b.user.followers, 0)
        const likes = selectedItems.reduce((a, b) => a + b.stat.likes, 0)
        const views = selectedItems.reduce((a, b) => a + b.stat.views, 0)
        const accounts = nonDuplicateAccounts.length

        const calculatedValues = {
          category: selectedFilter.title,
          followers: nFormatter(followers),
          accounts: nFormatter(accounts),
          likes: nFormatter(likes),
          views: nFormatter(views),
          posts: nFormatter(selectedItems.length)
        }

        const overviewData = [
          {
            text: `${selectedFilter.name} Influencers ${selectedFilter.suffix}`,
            value: calculatedValues.accounts
          },
          {
            text: `${selectedFilter.name} Posts ${selectedFilter.suffix}`,
            value: calculatedValues.posts
          },
          {
            text: `${selectedFilter.name} Likes ${selectedFilter.suffix}`,
            value: calculatedValues.likes
          },
          {
            text: `${selectedFilter.name} Views ${selectedFilter.suffix}`,
            value: calculatedValues.views
          },
          {
            text: `${selectedFilter.name} Followers ${selectedFilter.suffix}`,
            value: calculatedValues.followers
          },
        ]

        // if there's no item in overview section for this index
        if (!overviewSection[index]) {
          overviewSection[index] = []
        }

        // now merge them
        overviewSection[index] = overviewSection[index].concat(overviewData)

        // increment this value to find out where to put the item
        iterator++

        // put two bundles of overviewData together
        if (iterator % 2 === 0) {
          index++
        }
      }

      this.overviewSections.push(overviewSection)

      // calculate the post titles
      // we'll prefer sponsor username > mentions > hashtags
      const titlesArray = [
        // sponsors
        appliedFilters.sponsor ? appliedFilters.sponsor.data.inputs.any ? "Any" : appliedFilters.sponsor.data.inputs.username : null,

        // mentions
        ...appliedFilters.mentions.map((item) => '@' + item.data.inputs.username),

        // hashtags
        ...appliedFilters.hashtags.map((item) => '#' + item.data.inputs.hashtag)
      ].filter((item) => Boolean(item)).slice(0, 3)

      this.postTitles.push(titlesArray.length ? titlesArray.join(", ") : null)
    }

    // when this all is ready
    this.isEverythingReady = true
  }
}
</script>


<style lang="stylus">
.compare-table--collaborations
  min-width 1300px
  border-radius 0.25em
  overflow hidden

  .pdf-announcement-container
    position fixed
    z-index 30000
    height 100%
    width 100%
    left 0
    top 0

.compare-table-element--collaborations
  .post-row
    height 500px
    max-height 500px

  .post-card
    height 460px

    &__thumbnail
      width 100%
      height 250px
      background-size contain
      background-position 50%

  &.pdf-view
    position fixed
    z-index -10000
    left 0
    top 0
    height 100%
    width 1400px
    overflow-y auto

    .box
      height 600px
      // border 2px solid red
      display flex
      flex-direction column
</style>
